var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useState } from "react";
// material
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Box, Stack } from "@mui/material";
// helpers
import { getLinkSelectionData, toggleMark, withInlines, getCharCount, } from "./helpers";
import isHotkey from "is-hotkey";
// slate
import { Editable, withReact, Slate, ReactEditor, } from "slate-react";
import { createEditor, Node as SlateNode, Range } from "slate";
import { withHistory } from "slate-history";
// constants/types
import { ALL_CONTROLS, DEFAULT_CONTROLS, HOTKEYS } from "./constants";
import { ElementType } from "./types";
import { Context } from "./context";
// components
import { StyleControls, Element, Leaf } from "./components";
import { COLORS } from "../../../constants";
import { Dialog } from "../../Molecules";
import { IconButton, Text } from "../../Atoms";
var initialValue = [
    {
        type: ElementType.Paragraph,
        children: [{ text: "" }],
    },
];
var WYSIWYG = function (_a) {
    var _b = _a.controls, controls = _b === void 0 ? DEFAULT_CONTROLS : _b, dialogProps = _a.dialogProps, error = _a.error, onChange = _a.onChange, onBlur = _a.onBlur, editorStyles = _a.editorStyles, forceUpdateData = _a.forceUpdateData, readOnly = _a.readOnly, _c = _a.spellCheck, spellCheck = _c === void 0 ? true : _c, placeholder = _a.placeholder, helperNode = _a.helperNode, _d = _a.showCharCount, showCharCount = _d === void 0 ? false : _d, _e = _a.maxLength, maxLength = _e === void 0 ? 1000 : _e;
    var _f = __read(useState(0), 2), clearWysiwyg = _f[0], setClearWysiwyg = _f[1];
    var _g = __read(useState(false), 2), dialogOpen = _g[0], setDialogOpen = _g[1];
    var _h = __read(useState(false), 2), isFocused = _h[0], setFocused = _h[1];
    var _j = __read(useState(false), 2), linkDialogOpen = _j[0], setLinkDialog = _j[1];
    var _k = __read(useState({
        link: "",
        text: "",
    }), 2), linkData = _k[0], setLinkData = _k[1];
    /**
     * Parse all optional controls with the controls passed in to get the list to display
     */
    var controlsToUse = ALL_CONTROLS.reduce(function (acc, cur) {
        if (controls.includes(cur.style)) {
            acc.push(cur);
        }
        return acc;
    }, []);
    var renderElement = useCallback(function (props) { return _jsx(Element, __assign({}, props)); }, []);
    var renderLeaf = useCallback(function (props) { return _jsx(Leaf, __assign({}, props)); }, []);
    var editor = useMemo(function () { return withInlines(withHistory(withReact(createEditor()))); }, []);
    var contextData = useMemo(function () { return ({
        setLinkDialog: setLinkDialog,
        linkDialogOpen: linkDialogOpen,
        setLinkData: setLinkData,
        linkData: linkData,
    }); }, [setLinkDialog, linkDialogOpen, setLinkData, linkData]);
    // need to wait for all of the internal things to complete before clearing out wysiwyg
    // could have done via setTimeout also, but thought waiting for the render cycle was best
    useEffect(function () {
        if (clearWysiwyg > 0) {
            if (editor.selection) {
                editor.selection = {
                    anchor: { path: [0, 0], offset: 0 },
                    focus: { path: [0, 0], offset: 0 },
                };
            }
            editor.children = initialValue;
            editor.onChange();
        }
    }, [clearWysiwyg]);
    useEffect(function () {
        if (forceUpdateData && forceUpdateData.length) {
            if (editor.selection) {
                editor.selection = {
                    anchor: { path: [0, 0], offset: 0 },
                    focus: { path: [0, 0], offset: 0 },
                };
            }
            editor.children = forceUpdateData;
            editor.onChange();
        }
    }, [forceUpdateData]);
    /**
     * Display info dialog explaining wysiwyg
     */
    var toggleDialog = useCallback(function () {
        setDialogOpen(function (dialogState) { return !dialogState; });
    }, []);
    /**
     * force set focus state for testing
     */
    var setFocusedState = useCallback(function () {
        setFocused(true);
    }, []);
    /**
     * focus input when clicking anywhere in wysiwyg
     */
    var focusEditor = function () {
        ReactEditor.focus(editor);
        setFocusedState();
    };
    /**
     * cta for dialog
     */
    var buttonActions = useMemo(function () {
        return dialogProps
            ? [
                {
                    label: dialogProps.ctaText,
                    onClick: toggleDialog,
                },
            ]
            : [];
    }, []);
    var charCount = getCharCount(editor.children);
    /**
     * handle various hotkeys and prevent exceeding character limit
     */
    /* istanbul ignore next */
    var onKeyDown = function (event) {
        // error in slate that doesnt allow deletion via backspace of block element if its only
        // element in wysiwyg, this is the check for such a scenario to force reset the wysiwyg
        if (event.key === "Backspace") {
            var selection = editor.selection;
            if (selection &&
                selection.focus.offset === 0 &&
                selection.anchor.offset === 0 &&
                Range.isCollapsed(selection)) {
                var node = editor.children[selection.anchor.path[0]];
                var plain = editor.children
                    .map(function (n) { return SlateNode.string(n); })
                    .join("\n");
                if (plain.length <= 0 &&
                    ((node === null || node === void 0 ? void 0 : node.type) === ElementType.BulletedList ||
                        (node === null || node === void 0 ? void 0 : node.type) === ElementType.NumberedList)) {
                    // increment wysiwyg number to trigger clear on next render
                    setClearWysiwyg(function (r) { return r + 1; });
                }
            }
        }
        for (var hotkey in HOTKEYS) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            if (isHotkey(hotkey, event)) {
                event.preventDefault();
                var selected = HOTKEYS[hotkey];
                if (selected.type === "mark") {
                    toggleMark(editor, selected.value);
                }
                else if (selected.type === "element") {
                    if (selected.value === "link") {
                        var linkData_1 = getLinkSelectionData(editor);
                        setLinkData(linkData_1);
                        setLinkDialog(true);
                    }
                }
            }
        }
    };
    /**
     * calculate and display current character count
     */
    var weight = charCount >= maxLength ? 600 : 400;
    var charCountHint = showCharCount && !error && maxLength ? (_jsxs(Text, __assign({ "data-testid": "text-textfield-charcount", weight: weight, emphasis: "medium", variant: "body3" }, { children: [charCount, "/", maxLength, " Characters"] }))) : null;
    return (_jsx(Context.Provider, __assign({ value: contextData }, { children: _jsxs(Box, __assign({ onBlur: function (e) {
                if (!e.currentTarget.contains(e.relatedTarget)) {
                    setFocused(false);
                }
            }, "data-testid": "wysiwyg-blur-wrapper" }, { children: [_jsxs(Box, __assign({ sx: {
                        border: "1px solid ".concat(error
                            ? COLORS.SATURATED_RED
                            : isFocused
                                ? COLORS.P_MED_EMPHASIS
                                : COLORS.P_DISABLED),
                        borderRadius: "8px",
                    }, "data-testid": "wysiwyg-wrapper", onClick: focusEditor }, { children: [_jsxs(Slate, __assign({ editor: editor, value: initialValue, onChange: onChange }, { children: [!readOnly && (_jsxs(_Fragment, { children: [_jsxs(Stack, __assign({ direction: "row", px: 2, py: 1, alignItems: "center", justifyContent: "space-between", tabIndex: 0 }, { children: [_jsx(StyleControls, { controls: controlsToUse }), dialogProps && (_jsx(IconButton, __assign({ onClick: toggleDialog, "data-testid": "wysiwyg-info-icon" }, { children: _jsx(InfoOutlinedIcon, {}) })))] })), _jsx(Box, { sx: {
                                                bgcolor: isFocused
                                                    ? COLORS.P_MED_EMPHASIS
                                                    : COLORS.P_DISABLED,
                                                height: "1px",
                                                mx: 4,
                                            } })] })), _jsx(Box, __assign({ sx: __assign({ p: 3, lineHeight: "1.2" }, editorStyles), tabIndex: 0 }, { children: _jsx(Editable, { onBlur: onBlur, renderElement: renderElement, renderLeaf: renderLeaf, placeholder: placeholder, renderPlaceholder: function (_a) {
                                            var children = _a.children, attributes = _a.attributes;
                                            return (_jsx("div", __assign({}, attributes, { style: __assign(__assign({}, attributes.style), { opacity: 1, color: COLORS.P_DISABLED }) }, { children: _jsx("p", { children: children }) })));
                                        }, readOnly: readOnly, spellCheck: spellCheck, onFocus: setFocusedState, onKeyDown: onKeyDown }) }))] })), dialogProps && (_jsx(Dialog, { titleTextVariant: "subtitle1", title: dialogProps.title, "data-testid": "wysiwyg-info-dialog", handleClose: toggleDialog, content: _jsx(Text, __assign({ variant: "body1" }, { children: dialogProps.copy })), open: dialogOpen, buttonActions: buttonActions, PaperProps: {
                                sx: {
                                    width: "100%",
                                    maxWidth: 343,
                                },
                            } }))] })), (charCountHint || helperNode) && (_jsxs(Stack, __assign({ direction: "row", mt: 2, ml: 1 }, { children: [charCountHint, helperNode] })))] })) })));
};
export default WYSIWYG;
